import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import type { MouseEvent, ReactNode } from "react";

// ~40-line pushState router. The SPA fallback in wrangler.jsonc
// (not_found_handling: "single-page-application") makes deep links work on
// the CDN; this handles them in the client. Swap for react-router if the lab
// ever outgrows exact-path matching.

export function usePath(): string {
  const [path, setPath] = useState(() =>
    typeof location !== "undefined" ? location.pathname : "/",
  );
  useEffect(() => {
    const onPop = () => setPath(location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);
  return path;
}

function reducedMotion(): boolean {
  return (
    window.matchMedia("(prefers-reduced-motion: reduce)").matches ||
    document.documentElement.dataset.reducedMotion === "true"
  );
}

export function navigate(to: string): void {
  const go = () => {
    history.pushState(null, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
    window.scrollTo(0, 0);
  };

  // Walking between halls gets a door-crossing: the View Transitions API
  // crossfades the routes while elements that share a view-transition-name
  // (the Grand Tally) travel between pages. Chromium/Safari only — everyone
  // else, and reduced motion, gets the plain instant swap.
  const doc = document as Document & {
    startViewTransition?: (update: () => void) => unknown;
  };
  if (doc.startViewTransition && !reducedMotion()) {
    doc.startViewTransition(() => {
      // React schedules its render; the transition must snapshot the NEW
      // page inside this callback, so force the commit synchronously.
      flushSync(go);
    });
  } else {
    go();
  }
}

export function Link({
  to,
  className,
  children,
}: {
  to: string;
  className?: string;
  children: ReactNode;
}) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) {
      return; // let the browser handle new-tab / download clicks
    }
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} className={className} onClick={onClick}>
      {children}
    </a>
  );
}
